import { useState } from 'react';
import { Drawer, Select, Button, Space, Avatar, Input } from 'antd';
import {
  UserOutlined, EnvironmentOutlined,
  CalendarOutlined, ClockCircleOutlined,
  MessageOutlined, SendOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { STATUS_CONFIG, STATUS_OPTIONS } from '../dummyData';

dayjs.extend(relativeTime);

const { TextArea } = Input;
const THEME = '#008385';

const avatarColor = (name = '') => {
  const palette = ['#008385','#c0392b','#b45309','#1a7f4b','#6366f1','#0369a1','#7c3aed'];
  return palette[name.charCodeAt(0) % palette.length];
};

const buildCsrOptions = (csrList, assignedId, assignedName) => {
  const opts = csrList.map(c => ({ value: c.user_id, label: `${c.name} ${c.surname}` }));
  if (assignedId && !opts.find(o => o.value === assignedId)) {
    opts.unshift({ value: assignedId, label: assignedName || assignedId });
  }
  return opts;
};

const SectionTitle = ({ icon, children }) => (
  <div className="aj-drawer-section-title">{icon} {children}</div>
);

const InfoItem = ({ label, value, full }) => (
  <div className="aj-info-item" style={full ? { gridColumn: '1 / -1' } : {}}>
    <div className="aj-info-label">{label}</div>
    <div className="aj-info-value">{value || '—'}</div>
  </div>
);

export default function AbandonedDetailDrawer({
  open, session, csrList = [], isAdmin, currentUser,
  comments = [], onAddComment,
  onClose, onStatusChange, onAssign,
}) {
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting]   = useState(false);

  if (!session) return null;

  const handleSubmit = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 250));
    onAddComment(session.session_id, commentText);
    setCommentText('');
    setSubmitting(false);
  };

  const csrOptions = buildCsrOptions(csrList, session.assigned_csr_id, session.assigned_csr_name);
  const isMine     = session.assigned_csr_id === currentUser?.user_id;
  const canEdit    = isAdmin || isMine;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={500}
      styles={{
        header: { background: '#fff', borderBottom: '1px solid #f1f5f9', padding: '14px 20px' },
        body:   { padding: 0, background: '#f5f7fa', overflowY: 'auto' },
        footer: { padding: '14px 20px', borderTop: '1px solid #f1f5f9', background: '#fff' },
      }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34, height: 34, background: THEME, borderRadius: 9,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 16, flexShrink: 0,
          }}>
            <UserOutlined />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e', lineHeight: 1.2 }}>Session Detail</div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace', marginTop: 2 }}>
              {session.session_id?.slice(0, 36)}
            </div>
          </div>
        </div>
      }
      footer={
        <Button
          type="primary" block icon={<CalendarOutlined />} disabled
          style={{ background: THEME, borderColor: THEME, borderRadius: 8, height: 40, fontWeight: 600 }}
          title="Coming soon"
        >
          Re-book Session (Coming Soon)
        </Button>
      }
    >
      <div className="aj-drawer-body">

        {/* ── Patient Information ──────────────────────────────── */}
        <div className="aj-drawer-card">
          <SectionTitle icon={<UserOutlined />}>Patient Information</SectionTitle>
          <div className="aj-info-grid">
            <InfoItem label="Name"  value={session.patient_name || 'Unknown Patient'} />
            <InfoItem label="Phone" value={session.patient_phone} />
            <InfoItem label="Email" value={session.patient_email} full />
          </div>
        </div>

        {/* ── Booking Intent ───────────────────────────────────── */}
        <div className="aj-drawer-card">
          <SectionTitle icon={<EnvironmentOutlined />}>Booking Intent</SectionTitle>
          <div className="aj-info-grid">
            <InfoItem label="Clinic"       value={session.clinic_name || session.log_name} />
            <InfoItem label="Product"      value={session.product_name} />
            <InfoItem label="Module"       value={session.module || session.log_name} />
            <InfoItem label="Abandoned At" value={session.abandoned_at ? dayjs(session.abandoned_at).format('DD MMM YYYY HH:mm') : null} />
          </div>
        </div>

        {/* ── Follow-up Status ─────────────────────────────────── */}
        <div className="aj-drawer-card">
          <SectionTitle icon={<ClockCircleOutlined />}>Follow-up Status</SectionTitle>
          <Space direction="vertical" style={{ width: '100%' }} size={10}>

            {/* Status */}
            <div>
              <div className="aj-info-label" style={{ marginBottom: 5 }}>Status</div>
              {canEdit ? (
                <Select
                  value={session.follow_up_status}
                  style={{ width: '100%' }}
                  onChange={v => onStatusChange(session.session_id, v)}
                  options={STATUS_OPTIONS.map(o => ({
                    value: o.value,
                    label: <span style={{ color: STATUS_CONFIG[o.value]?.color, fontWeight: 600 }}>● {o.label}</span>,
                  }))}
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                  <span className={`aj-badge ${session.follow_up_status}`}>
                    <span className="aj-badge-dot" />
                    {STATUS_CONFIG[session.follow_up_status]?.label}
                  </span>
                  <span style={{ fontSize: 10, color: '#94a3b8' }}>Assign to yourself to change</span>
                </div>
              )}
            </div>

            {/* Assign CSR */}
            <div>
              <div className="aj-info-label" style={{ marginBottom: 5 }}>Assign CSR</div>

              {isAdmin && (
                <Select
                  labelInValue
                  value={session.assigned_csr_id
                    ? { value: session.assigned_csr_id, label: session.assigned_csr_name || session.assigned_csr_id }
                    : undefined
                  }
                  style={{ width: '100%' }}
                  placeholder="Search and assign CSR…"
                  allowClear showSearch optionFilterProp="label"
                  filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  onChange={v => onAssign(session.session_id, v?.value ?? null)}
                  options={csrOptions}
                />
              )}

              {!isAdmin && isMine && (
                <Select
                  value={session.assigned_csr_id}
                  style={{ width: '100%' }}
                  allowClear
                  onChange={v => onAssign(session.session_id, v ?? null)}
                  options={[{ value: currentUser.user_id, label: `${currentUser.name} (me)` }]}
                />
              )}

              {!isAdmin && session.assigned_csr_id && !isMine && (
                <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e' }}>{session.assigned_csr_name || session.assigned_csr_id}</span>
                  <span style={{ fontSize: 10, color: '#94a3b8' }}>Already assigned</span>
                </div>
              )}

              {!isAdmin && !session.assigned_csr_id && (
                <Button block
                  style={{ borderRadius: 8, borderColor: THEME, color: THEME, fontWeight: 600 }}
                  onClick={() => onAssign(session.session_id, currentUser?.user_id)}
                >
                  Assign to me
                </Button>
              )}
            </div>
          </Space>
        </div>

        {/* ── Notes & Activity ─────────────────────────────────── */}
        <div className="aj-notes-section">
          <SectionTitle icon={<MessageOutlined />}>Notes &amp; Activity</SectionTitle>

          {/* Input */}
          <TextArea
            value={commentText}
            onChange={e => setCommentText(e.target.value)}
            placeholder="Add a note or update…"
            autoSize={{ minRows: 3, maxRows: 6 }}
            style={{ borderRadius: 8, borderColor: '#e2e8f0', fontSize: 13, resize: 'none' }}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSubmit(); }}
          />
          <div className="aj-notes-input-row">
            <span className="aj-notes-hint">⌘ Ctrl+Enter to submit</span>
            <Button
              type="primary"
              className="aj-add-note-btn"
              icon={<SendOutlined />}
              loading={submitting}
              disabled={!commentText.trim()}
              onClick={handleSubmit}
            >
              Add Note
            </Button>
          </div>

          {/* Timeline */}
          {comments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0 6px', color: '#94a3b8', fontSize: 12 }}>
              No notes yet — add the first one above
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
              {[...comments].reverse().map(c => (
                <div key={c.id} className="aj-comment-item">
                  <Avatar size={30} style={{ background: avatarColor(c.author), flexShrink: 0, fontSize: 11, fontWeight: 700 }}>
                    {c.author?.[0]?.toUpperCase()}
                  </Avatar>
                  <div className="aj-comment-bubble">
                    <div className="aj-comment-meta">
                      <span className="aj-comment-author">{c.author}</span>
                      <span className="aj-comment-time" title={dayjs(c.timestamp).format('DD MMM YYYY HH:mm')}>
                        {dayjs(c.timestamp).fromNow()}
                      </span>
                    </div>
                    <div className="aj-comment-text">{c.text}</div>
                    {c.isDummy && <div className="aj-comment-auto">Auto-generated note</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </Drawer>
  );
}
